import { useState } from "react";
import { Search, SlidersHorizontal, X, AlertTriangle, Download, CalendarDays, Loader2, ChevronsLeft, ChevronsRight } from "lucide-react";

// All Shipments board (Delhivery-style): one status list with counts (each shipment in exactly one bucket),
// warning flags, saved views and a filters panel. Data: GET /api/v2/shipment-board.

export const RAIL = [
  { title: "Needs action", items: [["to_manifest", "To be manifested"], ["ready", "Ready for pickup"], ["pickup_late", "Pickup late", true], ["exception", "Exceptions (NDR)", true]] },
  { title: "Moving", items: [["transit", "In transit"], ["ofd", "Out for delivery"], ["rto", "Returns (RTO)"]] },
  { title: "Done", items: [["delivered", "Delivered"], ["cancelled", "Cancelled"]] },
  { title: "Safety net", items: [["review", "Needs review", true], ["all", "All shipments"]] },
];

export const FLAG_META = {
  at_risk: { label: "At risk", cls: "bg-orange-100 text-orange-800" },
  ndd_late: { label: "NDD late", cls: "bg-red-100 text-red-700" },
  stale: { label: "Tracking stale", cls: "bg-slate-200 text-slate-700" },
  ewb_missing: { label: "E-way bill missing", cls: "bg-amber-100 text-amber-800" },
  gst_unverified: { label: "GSTIN unverified", cls: "bg-yellow-100 text-yellow-800" },
  weight_mismatch: { label: "Weight mismatch", cls: "bg-fuchsia-100 text-fuchsia-800" },
  bill_not_final: { label: "Bill not finalised", cls: "bg-sky-100 text-sky-800" },
};

// saved views = one click to a bucket / flag / filter combination
export const VIEWS = [
  { key: "default", label: "Needs action", set: { bucket: "to_manifest", flag: "", filters: {} } },
  { key: "at_risk", label: "At risk", set: { flag: "at_risk", filters: {} } },
  { key: "ndd_late", label: "NDD late", set: { flag: "ndd_late", filters: {} } },
  { key: "stale", label: "Tracking stale", set: { flag: "stale", filters: {} } },
  { key: "enterprise", label: "Enterprise today", set: { bucket: "all", flag: "", filters: { channel: "enterprise" } } },
  { key: "credit", label: "Credit dues", set: { bucket: "delivered", flag: "", filters: { payment: "credit_due" } } },
  { key: "bill", label: "Bill not finalised", set: { flag: "bill_not_final", filters: {} } },
  { key: "gst", label: "GSTIN unverified", set: { flag: "gst_unverified", filters: {} } },
  { key: "ewb", label: "E-way bill missing", set: { flag: "ewb_missing", filters: {} } },
];

export const FILTER_GROUPS = [
  { key: "channel", title: "Channel", options: [["counter", "Counter / walk-in"], ["portal", "Customer portal"], ["staff", "Staff booking"], ["enterprise", "Enterprise"]] },
  { key: "network", title: "Network", options: [["own", "Own network"], ["3pl", "3PL (Delhivery)"]] },
  { key: "segment", title: "Segment", options: [["WALKIN", "Walk-in"], ["WALLET", "Wallet"], ["CREDIT", "Credit"]] },
  { key: "payment", title: "Payment", options: [["paid", "Paid"], ["credit_due", "Credit due"], ["unpaid", "Unpaid"]] },
  { key: "service", title: "Service", options: [["surface", "Surface"], ["express", "Express"], ["ndd", "NDD"]] },
  { key: "dest_state", title: "Destination state", options: [["Andhra Pradesh", "Andhra Pradesh"], ["Telangana", "Telangana"], ["Karnataka", "Karnataka"], ["Tamil Nadu", "Tamil Nadu"]] },
];
const OPTION_LABEL = Object.fromEntries(FILTER_GROUPS.flatMap((g) => g.options.map(([v, l]) => [`${g.key}:${v}`, `${g.title}: ${l}`])));

export const SEARCH_TYPES = [["all", "Any"], ["order", "Order ID"], ["awb", "AWB"], ["lr", "LR"], ["phone", "Phone"], ["customer", "Customer"], ["invoice", "Invoice no."]];

export function BoardToolbar({ board, setBoard, meta, onSearch, onExport }) {
  const [text, setText] = useState(board.search || "");
  const chips = Object.entries(board.filters || {}).filter(([, v]) => v).map(([k, v]) => ({ k, label: k === "origin_city" ? `Origin: ${v}` : k === "account" ? "Account selected" : OPTION_LABEL[`${k}:${v}`] || `${k}: ${v}` }));
  const activeView = VIEWS.find((v) => JSON.stringify({ bucket: v.set.bucket ?? board.bucket, flag: v.set.flag ?? "", filters: v.set.filters }) === JSON.stringify({ bucket: board.bucket, flag: board.flag || "", filters: board.filters || {} }));
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-3 space-y-3" data-testid="board-toolbar">
      <form onSubmit={(e) => { e.preventDefault(); onSearch(text.trim()); }} className="flex flex-wrap gap-2 items-center">
        <div className="flex flex-1 min-w-[260px] max-w-2xl h-10 border border-slate-300 rounded-lg overflow-hidden focus-within:border-brand-500">
          <label className="sr-only" htmlFor="board-search-type">Search by</label>
          <select id="board-search-type" value={board.searchType} onChange={(e) => setBoard((b) => ({ ...b, searchType: e.target.value }))}
            className="h-full border-r border-slate-200 bg-slate-50 px-2 text-xs font-semibold text-slate-700 focus:outline-none" data-testid="board-search-type">
            {SEARCH_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Search order, AWB, LR, phone, customer, invoice…" aria-label="Search shipments"
              className="w-full h-full pl-8 pr-8 text-sm focus:outline-none" data-testid="admin-shipments-search" />
            {board.search && <button type="button" onClick={() => { setText(""); onSearch(""); }} aria-label="Clear search" className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"><X className="w-4 h-4" /></button>}
          </div>
        </div>
        <button type="submit" className="h-10 px-4 bg-brand-500 text-white text-sm font-semibold rounded-lg">Search</button>
        <DateRange board={board} setBoard={setBoard} meta={meta} />
        <button type="button" onClick={() => setBoard((b) => ({ ...b, showFilters: true }))} data-testid="board-filters-btn"
          className="h-10 px-3 inline-flex items-center gap-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-800 hover:bg-slate-50">
          <SlidersHorizontal className="w-4 h-4" />Filters{chips.length ? ` · ${chips.length}` : ""}</button>
        {onExport && <DownloadMenu onExport={onExport} />}
      </form>
      <div className="flex gap-1 overflow-x-auto -mx-1 px-1" role="tablist" aria-label="Saved views">
        {VIEWS.map((v) => {
          const on = activeView?.key === v.key;
          const n = v.set.flag ? meta?.flags?.[v.set.flag] : undefined;
          return (
            <button key={v.key} type="button" role="tab" aria-selected={on} onClick={() => setBoard((b) => ({ ...b, bucket: v.set.bucket ?? b.bucket, flag: v.set.flag ?? "", filters: v.set.filters, search: "", page: 0 }))}
              data-testid={`view-${v.key}`}
              className={`shrink-0 h-8 px-3 text-xs font-semibold rounded-full whitespace-nowrap ${on ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
              {v.label}{n ? <span className={`ml-1.5 ${on ? "text-white/80" : "text-slate-400"}`}>{n}</span> : null}
            </button>);
        })}
      </div>
      {(chips.length > 0 || board.search) && (
        <div className="flex flex-wrap gap-1.5 items-center">
          {board.search && <span className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-indigo-50 text-indigo-800 text-xs font-semibold">Search: {board.search} (all statuses, dates and filters)</span>}
          {chips.map((c) => (
            <span key={c.k} className="inline-flex items-center gap-1 h-7 pl-2.5 pr-1 rounded-full bg-brand-50 text-brand-600 text-xs font-semibold">{c.label}
              <button type="button" aria-label={`Remove ${c.label}`} onClick={() => setBoard((b) => { const f = { ...b.filters }; delete f[c.k]; return { ...b, filters: f, page: 0 }; })}
                className="w-5 h-5 rounded-full hover:bg-brand-100 inline-flex items-center justify-center"><X className="w-3 h-3" /></button></span>))}
          {chips.length > 0 && <button type="button" onClick={() => setBoard((b) => ({ ...b, filters: {}, page: 0 }))} className="text-xs text-slate-500 hover:underline ml-1">Clear all</button>}
        </div>)}
    </div>
  );
}

export function BoardRail({ board, setBoard, meta }) {
  const counts = meta?.counts || {};
  const sumOk = meta ? Object.values(counts).reduce((a, b) => a + b, 0) === meta.total : true;
  const [folded, setFolded] = useState(() => { try { return localStorage.getItem("sendtoo_rail_collapsed") === "1"; } catch { return false; } });
  const fold = (v) => { setFolded(v); try { localStorage.setItem("sendtoo_rail_collapsed", v ? "1" : "0"); } catch { /* private mode */ } };
  const needs = ["to_manifest", "pickup_late", "exception", "review"].reduce((a, k) => a + (counts[k] || 0), 0);
  if (folded) {
    const cur = RAIL.flatMap((g) => g.items).find(([k]) => k === board.bucket);
    return (
      <aside aria-label="Shipment status (collapsed)" className="hidden sm:flex w-12 shrink-0 bg-white rounded-xl border border-slate-200 py-2 flex-col items-center gap-3 sm:sticky sm:top-4" data-testid="board-rail-collapsed">
        <button type="button" onClick={() => fold(false)} title="Show status list" aria-label="Show status list" data-testid="rail-expand"
          className="w-8 h-8 rounded-md bg-black text-white flex items-center justify-center"><ChevronsRight className="w-4 h-4" /></button>
        <span title="Needs action" className="min-w-[26px] text-center text-[11px] font-bold rounded-full px-1.5 py-0.5 bg-rose-600 text-white">{needs}</span>
        <span className="text-[10px] font-semibold text-slate-500 [writing-mode:vertical-rl] rotate-180 whitespace-nowrap">{board.flag || board.search ? "Filtered view" : (cur ? cur[1] : "Status")}</span>
      </aside>);
  }
  return (
    <aside aria-label="Shipment status" className="w-full sm:w-56 shrink-0 bg-white rounded-xl border border-slate-200 p-2 sm:sticky sm:top-4" data-testid="board-rail">
      <div className="hidden sm:flex items-center px-2.5 pt-1">
        <span className="text-xs font-bold text-slate-800">Status</span>
        <button type="button" onClick={() => fold(true)} title="Hide status list" aria-label="Hide status list" data-testid="rail-collapse"
          className="ml-auto w-7 h-7 rounded-md bg-black text-white flex items-center justify-center"><ChevronsLeft className="w-4 h-4" /></button>
      </div>
      {RAIL.map((sec) => (
        <div key={sec.title} className="py-1.5">
          <p className="px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{sec.title}</p>
          {sec.items.map(([k, label, hot]) => {
            const n = k === "all" ? meta?.total : counts[k];
            const on = !board.flag && !board.search && board.bucket === k;
            const red = hot && n > 0;
            return (
              <button key={k} type="button" onClick={() => setBoard((b) => ({ ...b, bucket: k, flag: "", search: "", page: 0 }))} data-testid={`bucket-${k}`}
                className={`w-full flex items-center gap-2 h-9 px-2.5 rounded-lg text-[13px] text-left ${on ? "bg-rose-50 font-bold text-slate-900" : "text-slate-700 hover:bg-slate-50"}`}>
                <span className="flex-1">{label}</span>
                <span className={`min-w-[24px] text-center text-[11px] font-bold rounded-full px-1.5 py-0.5 ${on ? "bg-rose-600 text-white" : red ? "bg-rose-100 text-rose-800" : "bg-slate-100 text-slate-600"}`}>{n ?? "–"}</span>
              </button>);
          })}
        </div>))}
      {!sumOk && <p className="m-2 text-[11px] text-red-700 bg-red-50 rounded-lg p-2 flex gap-1"><AlertTriangle className="w-3.5 h-3.5 shrink-0" />Counts don't add up - refresh</p>}
      <p className="px-2.5 pb-1 text-[10px] text-slate-400">{board.datesAll ? "Every bucket shows only shipments booked in the chosen dates." : "Open shipments show whatever their age. Dates apply to Delivered and Cancelled."}</p>
    </aside>
  );
}

export function FiltersDrawer({ board, setBoard }) {
  const [f, setF] = useState(board.filters || {});
  if (!board.showFilters) return null;
  const close = () => setBoard((b) => ({ ...b, showFilters: false }));
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex justify-end" onClick={close}>
      <div role="dialog" aria-label="Filters" className="w-full max-w-md h-full bg-white shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="filters-drawer">
        <div className="flex items-center px-5 py-4 border-b border-slate-200">
          <h2 className="font-heading font-bold text-lg">Filters</h2>
          <button type="button" onClick={close} aria-label="Close filters" className="ml-auto w-9 h-9 rounded-lg bg-slate-100 inline-flex items-center justify-center"><X className="w-4 h-4" /></button>
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {FILTER_GROUPS.map((g) => (
            <div key={g.key}>
              <p className="text-xs font-bold text-slate-700 mb-2">{g.title}</p>
              <div className="flex flex-wrap gap-1.5">
                {g.options.map(([v, l]) => {
                  const on = f[g.key] === v;
                  return <button key={v} type="button" onClick={() => setF((x) => ({ ...x, [g.key]: on ? undefined : v }))} data-testid={`filter-${g.key}-${v}`}
                    className={`h-8 px-3 rounded-full text-xs font-semibold border ${on ? "bg-brand-500 border-brand-500 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}>{l}</button>;
                })}
              </div>
            </div>))}
          <label className="block text-xs font-bold text-slate-700">Origin city
            <input value={f.origin_city || ""} onChange={(e) => setF((x) => ({ ...x, origin_city: e.target.value || undefined }))} placeholder="e.g. Visakhapatnam"
              className="mt-2 w-full h-10 border border-slate-300 rounded-lg px-3 text-sm font-normal" />
          </label>
        </div>
        <div className="flex gap-2 px-5 py-4 border-t border-slate-200">
          <button type="button" onClick={() => setF({})} className="flex-1 h-11 rounded-lg border border-slate-300 font-semibold text-sm">Clear all</button>
          <button type="button" onClick={() => setBoard((b) => ({ ...b, filters: Object.fromEntries(Object.entries(f).filter(([, v]) => v)), showFilters: false, page: 0 }))}
            className="flex-[2] h-11 rounded-lg bg-brand-500 text-white font-semibold text-sm" data-testid="filters-apply">Apply filters</button>
        </div>
      </div>
    </div>
  );
}

export function FlagChips({ s }) {
  const fl = s.board_flags || [];
  const due = s.promised_date ? new Date(s.promised_date + "T00:00:00") : null;
  return (
    <>
      {due && !["delivered", "cancelled"].includes(s.board_bucket) && (
        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${fl.includes("at_risk") ? "bg-orange-100 text-orange-800" : "bg-slate-100 text-slate-600"}`}
          title="Promised delivery (working days, Sunday and holidays not counted)" data-testid={`due-${s.id}`}>
          Due {due.toLocaleDateString("en-IN", { day: "numeric", month: "short" })}</span>)}
      {fl.filter((f) => f !== "gst_unverified").map((f) => (
        <span key={f} className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${FLAG_META[f]?.cls || "bg-slate-100 text-slate-600"}`} data-testid={`flag-${f}-${s.id}`}>{FLAG_META[f]?.label || f}</span>))}
      {s.board_bucket === "review" && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-rose-600 text-white" title={`Status "${s.status || "missing"}" does not fit any stage - check it`}>Needs review</span>}
    </>
  );
}

// ── calendar-level dates: quick picks + custom range; optional "apply to open shipments too" ──
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
export function presetRange(key, now = new Date()) {
  const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const add = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };
  switch (key) {
    case "today": return [iso(t), iso(t)];
    case "yesterday": return [iso(add(t, -1)), iso(add(t, -1))];
    case "week": { const dow = (t.getDay() + 6) % 7; return [iso(add(t, -dow)), iso(t)]; }               // Monday to today
    case "last_week": { const dow = (t.getDay() + 6) % 7; const mon = add(t, -dow - 7); return [iso(mon), iso(add(mon, 6))]; }
    case "month": return [iso(new Date(t.getFullYear(), t.getMonth(), 1)), iso(t)];
    case "last_month": return [iso(new Date(t.getFullYear(), t.getMonth() - 1, 1)), iso(new Date(t.getFullYear(), t.getMonth(), 0))];
    case "30d": return [iso(add(t, -29)), iso(t)];
    case "90d": return [iso(add(t, -89)), iso(t)];
    default: return null;
  }
}
const PRESETS = [["today", "Today"], ["yesterday", "Yesterday"], ["week", "This week"], ["last_week", "Last week"], ["month", "This month"],
  ["last_month", "Last month"], ["30d", "Last 30 days"], ["90d", "Last 90 days"], ["custom", "Custom dates"]];

function DateRange({ board, setBoard, meta }) {
  const preset = board.datePreset || "30d";
  const setPreset = (k) => {
    const r = presetRange(k);
    setBoard((b) => ({ ...b, datePreset: k, ...(r ? { dateFrom: r[0], dateTo: r[1] } : {}), page: 0 }));
  };
  return (
    <div className="flex flex-wrap items-center gap-1.5 text-xs text-slate-600" data-testid="board-dates">
      <label className="sr-only" htmlFor="board-preset">Booking dates</label>
      <div className="relative">
        <CalendarDays className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        <select id="board-preset" value={preset} onChange={(e) => setPreset(e.target.value)} data-testid="board-preset"
          className="h-10 pl-8 pr-2 border border-slate-300 rounded-lg bg-white text-sm font-semibold text-slate-700">
          {PRESETS.map(([k, l]) => <option key={k} value={k}>{l}</option>)}
        </select>
      </div>
      {preset === "custom" && (<>
        <label className="sr-only" htmlFor="board-from">From</label>
        <input id="board-from" type="date" value={board.dateFrom || meta?.date_from || ""} max={board.dateTo || undefined}
          onChange={(e) => { const v = e.target.value; setBoard((b) => ({ ...b, dateFrom: v, dateTo: b.dateTo && b.dateTo < v ? v : b.dateTo, page: 0 })); }} className="h-10 border border-slate-300 rounded-lg px-2" data-testid="board-from" />
        <span>to</span>
        <label className="sr-only" htmlFor="board-to">To</label>
        <input id="board-to" type="date" value={board.dateTo || meta?.date_to || ""} min={board.dateFrom || undefined}
          onChange={(e) => { const v = e.target.value; setBoard((b) => ({ ...b, dateTo: v, dateFrom: b.dateFrom && b.dateFrom > v ? v : b.dateFrom, page: 0 })); }} className="h-10 border border-slate-300 rounded-lg px-2" data-testid="board-to" />
      </>)}
      <label className="inline-flex items-center gap-1.5 h-10 px-2 rounded-lg border border-slate-200 cursor-pointer" title="Off: open shipments always show, however old (dates limit Delivered and Cancelled). On: every bucket shows only shipments booked in these dates.">
        <input type="checkbox" checked={!!board.datesAll} onChange={(e) => setBoard((b) => ({ ...b, datesAll: e.target.checked, page: 0 }))} data-testid="board-dates-all" />
        Apply to open shipments too
      </label>
    </div>
  );
}

function DownloadMenu({ onExport }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState("");
  const go = async (fmt) => { setBusy(fmt); try { await onExport(fmt); } finally { setBusy(""); setOpen(false); } };
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} data-testid="board-download"
        className="h-10 px-3 inline-flex items-center gap-1.5 border border-slate-300 rounded-lg text-sm font-semibold text-slate-800 hover:bg-slate-50">
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}Download</button>
      {open && (
        <div className="absolute right-0 mt-1 w-56 bg-white border border-slate-200 rounded-xl shadow-lg z-30 p-1" role="menu">
          <button type="button" role="menuitem" onClick={() => go("xlsx")} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-50" data-testid="download-xlsx">
            <b>Excel report</b><span className="block text-[11px] text-slate-500">This view, every row + a summary sheet</span></button>
          <button type="button" role="menuitem" onClick={() => go("csv")} className="w-full text-left px-3 py-2 rounded-lg text-sm hover:bg-slate-50" data-testid="download-csv">
            <b>CSV</b><span className="block text-[11px] text-slate-500">Same rows, for any spreadsheet</span></button>
        </div>)}
    </div>
  );
}
